import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import type Key from "@/app/types/common/key";
import { MenuItem } from "@/app/ui/navigation/types";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { keys } from "@/plugins/i18n";
import tenantStore from "@/store/modules/tenantStore";

interface ReportingMenuItem {
  key: string;
  reports: TableauReport[];
}

interface TableauReport {
  translationKey: Key;
  workbook: string;
  presentView: string | null;
  historicView: string | null;
  visible?: () => boolean;
}

export const reportingMenu = {
  root: "navigation_reporting_group",
  present: "present",
  historic: "historic",

  sales: "reporting_sales",
  production: "reporting_production",
  newProduction: "reporting_new_production",
  stock: "reporting_stock",
  batchTracking: "reporting_batch_tracking",
  tasks: "reporting_tasks",
  deposits: "reporting_deposits",
};

const tableauSalesReports: TableauReport[] = [
  {
    translationKey: keys.performance_dashboard,
    workbook: "Reporting",
    presentView: "PerformanceDashboard",
    historicView: "PerformanceDashboardHistoric",
  },
  {
    translationKey: keys.performance_graph,
    workbook: "Reporting",
    presentView: "PerformanceGraph",
    historicView: "PerformanceGraphHistoric",
  },
  {
    translationKey: keys.yoy_graph,
    workbook: "Reporting",
    presentView: "YOYGraph",
    historicView: "YOYGraphHistoric",
  },
  {
    translationKey: keys.performance_table,
    workbook: "Reporting",
    presentView: "PerformanceTable",
    historicView: "PerformanceTableHistoric",
  },
  {
    translationKey: keys.order_date_table,
    workbook: "Reporting",
    presentView: "OrderDateTable",
    historicView: "OrderDateTableHistoric",
  },
  {
    translationKey: keys.despatch_date_table,
    workbook: "Reporting",
    presentView: "DespatchDateTable",
    historicView: "DespatchDateTableHistoric",
  },
  {
    translationKey: keys.yoy_table,
    workbook: "Reporting",
    presentView: "YOYTable",
    historicView: "YOYTableHistoric",
  },
  {
    translationKey: keys.reporting_yoy_table_side_by_side,
    workbook: "Reporting",
    presentView: "YOYSide-by-Side",
    historicView: "YOYSide-by-SideHistoric",
  },
  {
    translationKey: keys.mat_table,
    workbook: "Reporting",
    presentView: "MATTable",
    historicView: "MATTable-Historic",
  },
  {
    translationKey: keys.performance_table_over_time,
    workbook: "Reporting",
    presentView: "PerformanceTableOverTime",
    historicView: "PerformanceTableOverTimeHistoric",
  },
  {
    translationKey: keys.performance_graph_by_dimension,
    workbook: "Reporting",
    presentView: "PerformanceGraphbyDimension",
    historicView: "PerformanceGraphbyDimensionHistoric",
  },
  {
    translationKey: keys.performance_by_map,
    workbook: "Reporting",
    presentView: "PerformanceMap",
    historicView: "PerformanceMapHistoric",
  },
  {
    translationKey: keys.customer_leakage_table,
    workbook: "Reporting",
    presentView: "CustomerLeakageTable",
    historicView: "CustomerLeakageTableHistoric",
  },
  {
    translationKey: keys.customer_leakage_last_delivery,
    workbook: "Reporting",
    presentView: "CustomerLeakageLastDelivery",
    historicView: "CustomerLeakageLastDeliveryHistoric",
  },
  {
    translationKey: keys.new_customer_table,
    workbook: "Reporting",
    presentView: "NewCustomerTable",
    historicView: "NewCustomerTableHistoric",
  },
  {
    translationKey: keys.litres_sold_trend_and_forecast,
    workbook: "Reporting",
    presentView: "Forecast-Trend",
    historicView: "Forecast-TrendHistoric",
  },
  {
    translationKey: keys.litres_sold_trend_and_seasonal,
    workbook: "Reporting",
    presentView: "Forecast-SeasonalandTrend",
    historicView: "Forecast-SeasonalandTrendHistoric",
  },
  {
    translationKey: keys.top_products_by_outlet,
    workbook: "Reporting",
    presentView: "TopProductsbyOutlet",
    historicView: "TopProductsbyOutletHistoric",
  },
  {
    translationKey: keys.top_outlets_by_product,
    workbook: "Reporting",
    presentView: "TopOutletsbyProduct",
    historicView: "TopOutletsbyProductHistoric",
  },
  {
    translationKey: keys.sales_by_day,
    workbook: "Reporting",
    presentView: "SalesbyDay",
    historicView: "SalesbyDayHistoric",
  },
  {
    translationKey: keys.sales_export_by_date,
    workbook: "Reporting",
    presentView: "SalesExportbyDate",
    historicView: "SalesExportbyDateHistoric",
  },
  {
    translationKey: keys.yoy_bar_chart,
    workbook: "Reporting",
    presentView: "YOYBarChart",
    historicView: "YOYBarChartHistoric",
  },
];

const tableauStockReports: TableauReport[] = [
  {
    translationKey: keys.reporting_stock_levels_report,
    workbook: "Reporting",
    presentView: "StockValuation",
    historicView: null,
  },
  {
    translationKey: keys.reporting_wip_valuation,
    workbook: "Reporting",
    presentView: "WIPValuation",
    historicView: null,
    visible: () => !tenantStore.state.tenantSettings.isMegaBatchesEnabled,
  },
  {
    translationKey: keys.reporting_wip_valuation,
    workbook: "Reporting",
    presentView: "WIPValuationNewProd",
    historicView: null,
    visible: () => tenantStore.state.tenantSettings.isMegaBatchesEnabled,
  },
  {
    translationKey: keys.reporting_future_stock_availability,
    workbook: "Reporting",
    presentView: "FutureStockAvailability",
    historicView: "FutureStockAvailabilityHistoric",
  },
  {
    translationKey: keys.reporting_number_of_weeks_stock_cover,
    workbook: "Reporting",
    presentView: "No_WeeksStockCover",
    historicView: "No_WeeksStockCover", // This is desired - as apparently it's more discoverable.
  },
  {
    translationKey: keys.reporting_allocated_stock_by_outlet,
    workbook: "Reporting",
    presentView: "AllocatedStockbyOutlet",
    historicView: "AllocatedStockbyOutletHistoric",
  },
  {
    translationKey: keys.reporting_litres_of_alcohol,
    workbook: "Reporting",
    presentView: "LitresofAlcohol",
    historicView: null,
  },
];

const tableauProductionReports: TableauReport[] = [
  {
    translationKey: keys.volume_graph,
    workbook: "Reporting",
    presentView: "ProductionVolumeGraph",
    historicView: "ProductionVolumeGraphHistoric",
  },
  {
    translationKey: keys.packaging_graph,
    workbook: "Reporting",
    presentView: "ProductionPackagingGraph",
    historicView: "ProductionPackagingGraphHistoric",
  },
  {
    translationKey: keys.summary_table,
    workbook: "Reporting",
    presentView: "ProductionSummaryTable",
    historicView: "ProductionSummaryTableHistoric",
  },
  {
    translationKey: keys.ingredient_summary_table,
    workbook: "Reporting",
    presentView: "ProductionIngredientSummaryTable",
    historicView: "ProductionIngredientSummaryTableHistoric",
  },
  {
    translationKey: keys.recipe_cost_vs_expectation,
    workbook: "Reporting",
    presentView: "ProductionRecipeCostvsExpectation",
    historicView: "ProductionRecipeCostvsExpectationHistoric",
  },
  {
    translationKey: keys.packaging_cost_vs_expectation,
    workbook: "Reporting",
    presentView: "ProductionPackagingCostvsExpectation",
    historicView: "ProductionPackagingCostvsExpectationHistoric",
  },
  {
    translationKey: keys.reporting_vessel_movements_table,
    workbook: "Reporting",
    presentView: "VesselMovementsTable",
    historicView: "VesselMovementsTable-Historic",
  },
  {
    translationKey: keys.reporting_qc_graph,
    workbook: "Reporting",
    presentView: "QCGraph",
    historicView: "QCGraphHistoric",
  },
  {
    translationKey: keys.reporting_qc_table,
    workbook: "Reporting",
    presentView: "QCTable",
    historicView: "QCTableHistoric",
  },
];

const tableauNewProductionReports: TableauReport[] = [
  {
    translationKey: keys.new_production_batch_summary,
    workbook: "Reporting",
    presentView: "NewProduction-BatchSummary",
    historicView: "NewProduction-BatchSummary-Historic",
  },
  {
    translationKey: keys.new_production_process_summary,
    workbook: "Reporting",
    presentView: "NewProduction-ProcessSummaryTable",
    historicView: "NewProduction-ProcessSummaryTable-Historic",
  },
  {
    translationKey: keys.ingredient_summary_table,
    workbook: "Reporting",
    presentView: "NewProduction-IngredientSummary",
    historicView: "NewProduction-IngredientSummaryHistoric",
  },
  {
    translationKey: keys.reporting_qc_graph,
    workbook: "Reporting",
    presentView: "QCGraph-NewProduction",
    historicView: "QCGraph-NewProductionHistoric",
  },
  {
    translationKey: keys.reporting_qc_table,
    workbook: "Reporting",
    presentView: "QCTable-NewProduction",
    historicView: "QCTable-NewProductionHistoric",
  },
];

const tableauBatchTrackingReports: TableauReport[] = [
  {
    translationKey: keys.reporting_delivery_batch_tracking,
    workbook: "Reporting",
    presentView: "DeliveryBatchTracking-Present",
    historicView: "DeliveryBatchTracking",
  },
  {
    translationKey: keys.reporting_traceable_items_used,
    workbook: "Reporting",
    presentView: "TraceableItemsUsed",
    historicView: "TraceableItemsUsedHistoric",
    visible: () => !tenantStore.state.tenantSettings.isMegaBatchesEnabled,
  },
  {
    translationKey: keys.reporting_traceable_items_used,
    workbook: "Reporting",
    presentView: "TraceableItemsUsedNewProd",
    historicView: "TraceableItemsUsedNewProdHistoric",
    visible: () => tenantStore.state.tenantSettings.isMegaBatchesEnabled,
  },
];

const tableauTasksReports: TableauReport[] = [
  {
    translationKey: keys.reporting_tasks_by_user,
    workbook: "Reporting",
    presentView: "TasksbyUser",
    historicView: "TasksbyUserHistoric",
  },
  {
    translationKey: keys.reporting_tasks_by_type,
    workbook: "Reporting",
    presentView: "TasksbyType",
    historicView: "TasksbyTypeHistoric",
  },
  {
    translationKey: keys.reporting_tasks_overdue,
    workbook: "Reporting",
    presentView: "OverdueTasks",
    historicView: "OverdueTasksHistoric",
  },
  {
    translationKey: keys.reporting_tasks_completed,
    workbook: "Reporting",
    presentView: "CompletedTasks",
    historicView: "CompletedTasksHistoric",
  },
];

const tableauDepositReports: TableauReport[] = [
  {
    translationKey: keys.reporting_deposit_returns,
    workbook: "Reporting",
    presentView: "DepositsReturns",
    historicView: "DepositsReturnsHistoric",
  },
];

export const buildReportingMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: reportingMenu.root,
    icon: "description",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuReportingView]),
    subMenus: (
      [
        {
          key: reportingMenu.sales,
          reports: tableauSalesReports,
        },
        {
          key: reportingMenu.stock,
          reports: tableauStockReports,
        },
        {
          key: reportingMenu.production,
          reports: tableauProductionReports,
        },
        {
          key: reportingMenu.newProduction,
          reports: tableauNewProductionReports,
        },
        {
          key: reportingMenu.batchTracking,
          reports: tableauBatchTrackingReports,
        },
        {
          key: reportingMenu.tasks,
          reports: tableauTasksReports,
        },
        {
          key: reportingMenu.deposits,
          reports: tableauDepositReports,
        },
      ] as ReportingMenuItem[]
    ).map((menuDetails) => {
      return MenuItem.forBranch({
        name: menuDetails.key,
        subMenus: [
          MenuItem.forBranch({
            name: `${menuDetails.key}-present`,
            translationKey: "present",
            subMenus: menuDetails.reports
              .filter((report) => report.presentView)
              .map((report) => {
                return MenuItem.fromOpts({
                  name: `${menuDetails.key}-present-${report.presentView}`,
                  url: `/reporting/reports/${report.workbook}/${report.presentView}`,
                  translationKey: report.translationKey.value,
                  view: (): Promise<any> =>
                    import(
                      /* webpackChunkName: "reporting" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
                    ),
                  componentProps: {
                    workbook: report.workbook,
                    view: report.presentView,
                    translationKey: report.translationKey,
                  },
                  visible: report.visible,
                });
              }),
          }),
          MenuItem.forBranch({
            name: `${menuDetails.key}-historic`,
            translationKey: "historic",
            subMenus: menuDetails.reports
              .filter((report) => report.historicView)
              .map((report) => {
                return MenuItem.fromOpts({
                  name: `${menuDetails.key}-historic-${report.historicView}`,
                  url: `/reporting/reports/${report.workbook}/${report.historicView}`,
                  translationKey: report.translationKey.value,
                  view: (): Promise<any> =>
                    import(
                      /* webpackChunkName: "reporting" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
                    ),
                  componentProps: {
                    workbook: report.workbook,
                    view: report.historicView,
                    translationKey: report.translationKey,
                  },
                  visible: report.visible,
                });
              }),
          }),
        ],
      });
    }),
    requiredUserPermissions: [EnumUserPermission.reporting],
  }),
];
